/**
 * Deciding what to write when an estimator overrules the model.
 *
 * Pulled out of `supabaseRepo` as a pure function for one reason: the rule it
 * encodes is easy to state, easy to get wrong, and destroys data silently when
 * it is wrong. It deserves to be tested directly rather than through a mocked
 * database client.
 *
 * The rule: `proposed_operation` captures the MODEL's answer, and is written
 * exactly once — on the first override. A second override must leave it alone.
 * Copying `operation` into it again would replace the model's answer with the
 * previous HUMAN answer, and the row would then claim the model proposed
 * something a person actually chose. The label would look intact and be a lie,
 * which is worse than losing it outright.
 */

export interface ProposalRow {
  /** The effective operation as the row currently stands. */
  operation: string;
  /** The model's answer, if this row has already been overruled. */
  proposed_operation?: string | null;
}

export interface OperationPatch {
  operation: string;
  operation_decided_at: string;
  operation_decided_by?: string;
  /** Present only on the first override. */
  proposed_operation?: string;
}

/**
 * What to write, or `null` for "write nothing".
 *
 * Returns null when the estimator lands back on the model's own answer with no
 * override on record. That is agreement, not a label, and storing it would
 * invent a disagreement that never happened — inflating the count of times the
 * model was corrected, which is the number that decides where prompt budget
 * goes in `shopPractice`.
 */
export function operationPatch(
  row: ProposalRow,
  operation: string,
  at: string,
  by?: string,
): OperationPatch | null {
  const alreadyOverruled = Boolean(row.proposed_operation);

  if (row.operation === operation && !alreadyOverruled) return null;

  const patch: OperationPatch = { operation, operation_decided_at: at };
  if (by) patch.operation_decided_by = by;
  // First override only. See the rule above.
  if (!alreadyOverruled) patch.proposed_operation = row.operation;
  return patch;
}

/**
 * Did a human genuinely disagree with the model on this row?
 *
 * An estimator who overrules and then changes their mind back leaves
 * `proposed_operation` set and equal to `operation`. That is a round trip, not
 * a correction, and counting it would overstate how often the model is wrong.
 */
export function isOverruled(row: ProposalRow): boolean {
  return Boolean(row.proposed_operation) && row.proposed_operation !== row.operation;
}

/**
 * Postgres' code for "column does not exist".
 *
 * The label columns arrive in migration 20260829000100, which needs peer auth
 * on the host and therefore lands separately from a frontend deploy. Between
 * the two, every override would otherwise raise a save error at an estimator
 * who did nothing wrong and cannot fix it.
 */
export const UNDEFINED_COLUMN = '42703';

/**
 * Is this the specific, expected "migration has not landed yet" failure?
 *
 * Narrow on purpose. Swallowing every error here would hide exactly the thing
 * this feature exists to prevent — a label quietly failing to be written — so
 * only this one code is tolerated and everything else still surfaces.
 */
export function isMissingLabelColumn(err: { code?: string; message?: string } | null): boolean {
  if (!err) return false;
  return err.code === UNDEFINED_COLUMN
    || /proposed_operation|operation_decided/.test(err.message ?? '');
}
